import { useMutation } from '@tanstack/react-query';
import { publicPost } from '@/lib/api/publicClient';
import { publicReviews } from '@/lib/api/endpoints';
import type { ReviewPlatformId } from './useReviewLanding';

export interface SubmitReviewInput {
  rating: number; // 1..5
  feedbackText?: string;
  selectedPlatform?: ReviewPlatformId;
  /**
   * The customer ticked "share this with future customers". Sent on every
   * submit, including `false`, so the stored row records a decision rather
   * than an absence.
   */
  publishConsent?: boolean;
}

interface SubmitReviewEnvelope {
  data: unknown;
}

/**
 * Commits the customer's rating, at any rating 1-5, with optional private
 * feedback and an optional chosen platform in the same request.
 *
 * There is no star-dependent split here and there must not be one again: the
 * server honours `selectedPlatform` at every rating and stamps the click
 * alongside the rating in one transaction, so a one-star customer who taps
 * Google is recorded as having gone to Google. `usePlatformClick` is the
 * *other* case — a platform tap made before any rating exists, which records
 * the click without consuming the single-claim submit slot.
 */
export function useSubmitReview(token: string) {
  return useMutation({
    mutationFn: (input: SubmitReviewInput) =>
      publicPost<SubmitReviewEnvelope, SubmitReviewInput>(publicReviews.submit(token), input),
  });
}
